import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";
import { ApiRequestError, describeError } from "@/data/api";
import { AuthError, sendSignInCode, signInWithCode } from "@/data/auth-client";
import { CLOUD_URL, isHostedWeb, saveCloudSession } from "@/data/session";
import { PairingScreen } from "./PairingScreen";

/** "Safari on Mac", for the device list in the account. */
export function browserDeviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /iPhone|iPad/.test(ua) ? "iPhone" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /Linux/.test(ua) ? "Linux" : "web";
  return `${browser} on ${os}`;
}

const message = (e: unknown) =>
  e instanceof AuthError ? e.message : e instanceof ApiRequestError && e.code !== "network" && e.message ? e.message : describeError(e);

export function SignInForm({ onCode, onVerify }: { onCode: (email: string) => Promise<void>; onVerify: (email: string, code: string) => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          if (!sent) {
            await onCode(email.trim());
            setSent(true);
          } else await onVerify(email.trim(), code.trim());
        });
      }}
    >
      {!sent ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signin-email">Email</Label>
          <Input
            id="signin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            spellCheck={false}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signin-code">Code sent to {email.trim()}</Label>
          <Input
            id="signin-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            autoFocus
            className="tabular tracking-[0.3em]"
          />
        </div>
      )}
      {error && (
        <p role="alert" className="rounded-md bg-urgent-soft px-3 py-2 text-[13px]">
          {error}
        </p>
      )}
      <Button type="submit" variant="default" size="lg" disabled={busy || (!sent ? !email.includes("@") : code.trim().length !== 6)}>
        {busy ? (sent ? "Signing in…" : "Sending…") : sent ? "Sign in" : "Email me a code"}
      </Button>
      {sent && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-center"
          disabled={busy}
          onClick={() => {
            setSent(false);
            setCode("");
            setError(null);
          }}
        >
          Use a different email
        </Button>
      )}
    </form>
  );
}

/** First screen of the web app: sign in to Desk cloud. */
export function SignInScreen({ onSignedIn, reason }: { onSignedIn: () => void; reason?: string }) {
  const [pairing, setPairing] = useState(false);
  if (pairing) return <PairingScreen onPaired={onSignedIn} />;
  return (
    <div className="flex h-full items-center justify-center bg-background p-6">
      <div className="w-full max-w-[380px]">
        <h1 className="mb-6 font-display text-[26px] font-semibold tracking-[-0.015em]">Sign in to Desk</h1>
        {reason && (
          <p role="status" className="mb-4 rounded-md bg-muted px-3 py-2 text-[13px]">
            {reason}
          </p>
        )}
        <SignInForm
          onCode={sendSignInCode}
          onVerify={async (email, code) => {
            const res = await signInWithCode(email, code, browserDeviceName());
            saveCloudSession(CLOUD_URL, res.token, res.email);
            onSignedIn();
          }}
        />
        {!isHostedWeb() && (
          <Button variant="link" size="sm" className="mt-6 px-0 text-muted-foreground" onClick={() => setPairing(true)}>
            Connect to a Desk service with a token
          </Button>
        )}
      </div>
    </div>
  );
}
